import { useState, useEffect } from "react";

import { useRecord, useRecords } from "@latticexyz/stash/react";
import mudConfig from "contracts/mud.config";
import { stash } from "../mud/stash";
import { useSessionPlayer } from "../SessionPlayerContext";

import { gameStates, getGameStateName, factionNames, secretStates } from "../config.js";

import { StateHome } from "./Game/States/StateHome";
import { StateRoll } from "./Game/States/StateRoll";
import { StateRead } from "./Game/States/StateRead";
import { StateWrite } from "./Game/States/StateWrite";
import { StateFork } from "./Game/States/StateFork";
import { StateInn } from "./Game/States/StateInn";
import { StateSelector } from "./Game/Persistent/StateSelector";
import { Guide } from "./Game/Persistent/Guide";
import { SecretGuide } from "./Game/Persistent/SecretGuide";
import { FangoUI } from "./Game/Persistent/FangoUI";
import { useLeaderboard } from "./LeaderboardContext";
import "../assets/styles/game.css";

import { GameWon } from "../components/GameWon";

import ExtraAudio from "../ExtraAudio.js";

import { useGuide } from "./GuideContext";
import { getGuideText, textConst } from "../text";

export const Game = () => {  
  const { address: userAddress } = useSessionPlayer();

  const [gameState, setGameState] = useState( gameStates.HOME );
  const [fork, setFork] = useState({
    hasFork: false,
    baseStory: {}
  });

  const {guideState, setGuideState} = useGuide();
  const {leaderboardState, setLeaderboardState} = useLeaderboard();
  const [secretState, setSecretState] = useState(0);
  const [showSecret, setShowSecret] = useState(false);
  const [showWon, setShowWon] = useState(false);

  /* RANKING CALCULATION CODE */
  /* ToDo: check if this is slowing things down, and maybe move to a custom hook?
  https://react.dev/learn/reusing-logic-with-custom-hooks
  */
  const allStories = useRecords({
    stash,
    table: mudConfig.tables.StoryPart
  });
  const allPlayers = useRecords({
    stash,
    table: mudConfig.tables.Player
  })
  const allFactions = useRecords({
    stash,
    table: mudConfig.tables.Fraction
  });

  useEffect( () => { 
    const updateRankingTimer = setInterval( () => {
      const listingTemp = {};
      // console.log('START RANKING CALCULATION. allStories = ', allStories, 'listingTemp = ', listingTemp );
      
      for( const story in allStories ){
        const author = allStories[story].author;
        const posVotes = allStories[story].posVotes;
        const negVotes = allStories[story].negVotes;
    
        if( !listingTemp[author] ){
          listingTemp[author] = 0;
        }
        listingTemp[author] += posVotes - negVotes;
      }     
    
      const ranking = Object.values( listingTemp ).map( (value, index) => {
        const authorAddr = Object.keys( listingTemp )[index];
        const author = allPlayers.find( ( p ) => p.counterValue === authorAddr );
        const faction = allFactions.find( ( f ) => f.counterValue === author.fraction );
        return {
          author,
          faction,
          votes: value
        };
      }).sort(( a, b ) => {
        return b.votes - a.votes;
      });
    
      setLeaderboardState({ranking});
    }, 1000 );
    return () => {
      clearInterval( updateRankingTimer );
    }
  }, [allStories] )

  const timeouts = (userAddress) 
    ? useRecord({
      stash, 
      table: mudConfig.tables.PlayerTimes, 
      key: {counterValue: userAddress }
    })
    : false;

  const player = (userAddress) ? useRecord({
    stash, 
    table: mudConfig.tables.Player, 
    key: { counterValue: userAddress } 
  }) : false;
  const playerFaction = (player) ? useRecord({
    stash, 
    table: mudConfig.tables.Fraction, 
    key: { counterValue: player.fraction } 
  })?.fractionName : false;


  useEffect(() => {
    // console.log( 'GAME STATE CHANGE, to:', getGameStateName( gameState ) );
    ExtraAudio.setState( gameState );
  }, [gameState]) 

  const onTransactionFail = () => {
    setGuideState({
      ...guideState,
      statement: getGuideText( playerFaction, textConst.TRANSACTIONFAIL ) 
    });
  }

  useEffect( () => {    
    if( player.secret > 0 && player.secret !== secretState ){
      setSecretState( player.secret );
      setShowSecret( true );
    }
  }, [player] );

  return (
    <>
    {(gameState === gameStates.HOME) 
    
      ? <div className="player-create--fango-img player-create--fango-img__closer"></div> 
      : (gameState === gameStates.INN) ? <div className="player-create--fango-img player-create--fango-img__inn"></div> : ''
    }
    <article className={`game fango-grid game__state-${getGameStateName(gameState)}`}>
      <section className="game--play gi-ml">      
        <StateHome 
          shouldRender={gameState === gameStates.HOME}
        />
        <StateRoll 
          shouldRender={gameState === gameStates.ROLL}
          onRollComplete={ () => {        
            setGameState( gameStates.WRITE );
          } }
          onRollFail={ onTransactionFail }
        />
        <StateRead 
          shouldRender={gameState === gameStates.READ}
          onForkStory={ ( story ) => {
            setFork({
              hasFork: true,
              baseStory: story
            });
            setGameState( gameStates.FORK );
          }}
          onVoteFail={ onTransactionFail }
        />    
        <StateWrite 
          shouldRender={gameState === gameStates.WRITE}
          onWriteComplete={()=>{            
            setGameState( gameStates.HOME );           
            setGuideState({
              ...guideState,
              statement: getGuideText( playerFaction, textConst.WRITEPUBLISH, [{find: '<username>', replace: player.name }] ) 
            });
          }}
          onWriteFail={ onTransactionFail }
        />
        {/* <StateFork 
          shouldRender={gameState === gameStates.FORK}
          baseStory={ fork.baseStory }
          onForkComplete={()=>{
            setFork( {
              hasFork: false,
              baseStory: {}
            });
            setGameState( gameStates.READ );
          }}
          onClose={() => {
            setFork( {
              hasFork: false,
              baseStory: {}
            });
            setGameState( gameStates.READ );
          }}
        /> */}
        <StateInn 
          shouldRender={gameState === gameStates.INN}
        />
         {(showWon) ? <GameWon onClick={()=>{ setShowWon( false ) }}/> : '' }
      </section>
      <section className="game--interface gi-mr">
        <div className="gi-mr--pb">
          <FangoUI />
          <StateSelector      
            onSelect={( newState ) => {
              setGameState( newState );
              setShowSecret( false );
            }}
          />
        </div>
        <div className="gi-mr--ps">
          <div className="game-interface-clocks">
           
          </div>
        </div>
        <div className="gi-mr--gb">

        </div>
        <div className="gi-mr--g">
          <Guide/>
          <SecretGuide 
            showState={showSecret}
            onFollow={ () => {
              window.open('https://dmstfctn.itch.io/godmode-epochs');              
              setShowWon(true);
              setShowSecret( false );
            }}
          />        
        </div>
      </section>    
    </article>
  </>
  )
}